import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Select, Badge, TagsInput, Tabs } from '@mantine/core';
import { IconCheck, IconMailFast } from '@tabler/icons-react';
import { useTenantBusinesses, useSendBulkEmail, useEmailTemplates, useAllTariffEmailLogs } from '@/hooks/api-hooks';
import { TenantBusiness, Business } from '@turizi/shared';
import dayjs from 'dayjs';
import { SkeletonList } from '@/components/snippets';
import FilteredBusinessesLayout from '@/components/filtered-businesses-layout';
import { localTenant } from '@/services/session-service';


export default function EmailCampaignsPage() {
    const { t } = useTranslation();
    const tenant = localTenant();

    const senderEmails = useMemo(() => {
        return Array.from(
            new Set([
                tenant?.settings?.email,
                ...(tenant?.settings?.sender_emails || [])
            ].filter(Boolean))
        );
    }, [tenant?.settings?.email, tenant?.settings?.sender_emails]);

    const [selectedFromEmail, setSelectedFromEmail] = useState<string>(senderEmails[0] || '');

    const {
        data: tenantBusinessesData,
        isPending: isLoadingBusinesses
    } = useTenantBusinesses();
    const {
        data: emailTemplatesData,
        isPending: isLoadingTemplates
    } = useEmailTemplates();
    const {
        data: emailLogsData,
        refetch: refetchLogs,
        isPending: isLoadingLogs
    } = useAllTariffEmailLogs();
    const {
        mutate: sendBulkEmailMutation,
        isPending: isSendingEmail
    } = useSendBulkEmail();

    const [selectedBusinesses, setSelectedBusinesses] = useState<string[]>([]);
    const [templateId, setTemplateId] = useState<string | null>(null);
    const [manualEmails, setManualEmails] = useState<string[]>([]);
    const [sendStatus, setSendStatus] = useState<'idle' | 'success' | 'error'>('idle');

    const handleSelectionChange = useCallback((ids: string[]) => {
        setSelectedBusinesses(ids);
    }, []);

    const validTenantBusinesses = useMemo(() => {
        return (tenantBusinessesData?.data || []).filter((tb: TenantBusiness) => tb.business_id);
    }, [tenantBusinessesData?.data]);

    const tenantBusinessesByIdMap = useMemo(() => {
        const map = new Map<string, TenantBusiness>();
        validTenantBusinesses.forEach((tb: TenantBusiness) => {
            if (tb.business_id) {
                map.set(String(tb.business_id), tb);
            }
        });
        return map;
    }, [validTenantBusinesses]);

    const businesses = useMemo(() => {
        return validTenantBusinesses.map((tb: TenantBusiness) => ({
            ...tb.business,
            tenant_business: tb
        })) as Business[];
    }, [validTenantBusinesses]);

    const templates = useMemo(() => {
        if (!Array.isArray(emailTemplatesData?.data)) return [];
        const templatesMap = new Map();
        emailTemplatesData.data.forEach((tmpl: any) => {
            if (tmpl.id) {
                templatesMap.set(String(tmpl.id), { value: String(tmpl.id), label: tmpl.name });
            }
        });
        return Array.from(templatesMap.values());
    }, [emailTemplatesData?.data]);

    const logs = emailLogsData?.data || [];

    const handleSend = () => {
        if (selectedBusinesses.length === 0 && manualEmails.length === 0) return;

        sendBulkEmailMutation({
            businessIds: selectedBusinesses.length === validTenantBusinesses.length ? 'all' : selectedBusinesses.map(Number),
            templateId: templateId ? Number(templateId) : undefined,
            manualEmails: manualEmails.length > 0 ? manualEmails : undefined,
            fromEmail: selectedFromEmail
        }, {
            onSuccess: (data) => {
                setSendStatus('success');
                refetchLogs();
            },
            onError: (error) => {
                setSendStatus('error');
            }
        });
    };

    const selectedRecipientsBadges = useMemo(() => {
        if (selectedBusinesses.length === 0 && manualEmails.length === 0) return null;

        return (
            <div className="flex flex-wrap gap-0.5 max-h-40 overflow-y-auto">
                {!selectedBusinesses.includes('all') && selectedBusinesses.map(id => {
                    const tb = tenantBusinessesByIdMap.get(id);
                    if (!tb) return null;
                    const emails = tb.business?.emails ? Object.values(tb.business.emails) as string[] : [];
                    const hasEmails = emails.length > 0;
                    const emailText = emails.length > 1 ? `(${emails.length} emails)` : emails.length === 1 ? `(${emails[0]})` : '';
                    return (
                        <div
                            key={id}
                            className={`text-xxs px-3 py-1 rounded-full ${hasEmails ? 'bg-gray-50 text-gray-600' : 'bg-red-50 text-red-600'}`}
                        >
                            {tb.business?.name} {emailText}
                        </div>
                    );
                })}

                {manualEmails.map(email => (
                    <Badge key={email} variant="outline" color="gray" className="normal-case">{email}</Badge>
                ))}
            </div>
        );
    }, [selectedBusinesses, manualEmails, tenantBusinessesByIdMap]);

    return (
        <div className="panel-content">
            <Tabs defaultValue="campaign" color="black">
                <Tabs.List className="mb-4">
                    <Tabs.Tab value="campaign" className="font-medium text-sm">
                        {t('new_campaign')}
                    </Tabs.Tab>
                    <Tabs.Tab value="history" className="font-medium text-sm">
                        {t('history')}
                    </Tabs.Tab>
                </Tabs.List>

                <Tabs.Panel value="campaign">
                    {sendStatus === 'success' ? (
                        <div className="solid-box-border text-center py-16 space-y-4">
                            <div className="mx-auto w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center">
                                <IconCheck size={32} />
                            </div>
                            <h2 className="text-xl font-bold text-gray-800">{t('campaign_sent_successfully')}</h2>
                            <p className="text-gray-500 max-w-md mx-auto">
                                {t('campaign_sent_description')}
                            </p>
                            <div className="pt-6">
                                <Button
                                    color="black"
                                    onClick={() => {
                                        setSendStatus('idle');
                                        setSelectedBusinesses([]);
                                        setManualEmails([]);
                                        setTemplateId(null);
                                    }}
                                >
                                    {t('create_another_campaign')}
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div className='flex gap-4 items-start'>
                            <div className='flex-1 solid-box-border'>
                                <FilteredBusinessesLayout
                                    businesses={businesses}
                                    isLoading={isLoadingBusinesses}
                                    selectedIds={selectedBusinesses}
                                    onSelectionChange={handleSelectionChange}
                                />
                            </div>
                            <div className="solid-box-border space-y-4 w-100 sticky top-4 shrink-0">
                                <div className="space-y-4 pb-4 border-b border-gray-100">
                                    {senderEmails.length > 0 && (
                                        <Select
                                            label={t('select_sender_email')}
                                            data={senderEmails}
                                            value={selectedFromEmail}
                                            onChange={(val) => setSelectedFromEmail(val || '')}
                                            allowDeselect={false}
                                        />
                                    )}
                                    <Select
                                        className='font-bold'
                                        label={t('template')}
                                        data={templates}
                                        value={templateId || ''}
                                        onChange={setTemplateId}
                                    />
                                    <div>
                                        <div className='text-xs text-gray-500 mb-1'>
                                            {t('manual_emails')} <span className="text-gray-300">({t('optional')})</span>
                                        </div>
                                        <TagsInput
                                            value={manualEmails}
                                            onChange={setManualEmails}
                                            clearable
                                        />
                                    </div>
                                </div>

                                <div className="flex items-center justify-between">
                                    <div className="text-xs font-semibold text-gray-500">
                                        {t('selected_recipients')}
                                    </div>
                                    <div className="text-sm font-bold bg-gray-100 px-3 py-1 rounded-full">
                                        {selectedBusinesses.length + manualEmails.length}
                                    </div>
                                </div>

                                {selectedRecipientsBadges}

                                {sendStatus === 'error' && (
                                    <div className="bg-red-50 text-red-600 p-3 rounded-md text-sm">
                                        {t('campaign_send_error')}
                                    </div>
                                )}

                                <div className="pt-2">
                                    {sendStatus === 'error' ? (
                                        <div className="flex flex-col gap-2 w-full">
                                            <Button
                                                fullWidth
                                                color="red"
                                                variant="light"
                                                onClick={handleSend}
                                                loading={isSendingEmail}
                                            >
                                                {t('retry_campaign')}
                                            </Button>
                                            <Button
                                                fullWidth
                                                color="gray"
                                                variant="subtle"
                                                onClick={() => setSendStatus('idle')}
                                                disabled={isSendingEmail}
                                            >
                                                {t('cancel')}
                                            </Button>
                                        </div>
                                    ) : (
                                        <Button
                                            fullWidth
                                            color="black"
                                            onClick={handleSend}
                                            loading={isSendingEmail}
                                            disabled={(selectedBusinesses.length === 0 && manualEmails.length === 0) || !templateId}
                                            leftSection={<IconMailFast size={18} />}
                                        >
                                            {t('send_campaign')}
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </Tabs.Panel>

                <Tabs.Panel value="history">
                    <div className="space-y-4">
                        {isLoadingLogs && <SkeletonList />}
                        {!isLoadingLogs && (
                            <div className="subtle-table">
                                <table>
                                    <thead>
                                        <tr>
                                            <th>{t('date')}</th>
                                            <th>{t('campaign')}</th>
                                            <th>{t('sender')}</th>
                                            <th>{t('business')}</th>
                                            <th>{t('sent_to')}</th>
                                            <th>{t('status')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {logs.map((log: any) => (
                                            <tr key={log.id}>
                                                <td style={{ fontSize: '12px' }}>{dayjs(log.created_at).format('DD/MM/YYYY HH:mm')}</td>
                                                <td style={{ fontSize: '12px' }}>{log.template_used || t('default_tariff_template')}</td>
                                                <td style={{ fontSize: '12px' }}>
                                                    {log.from_name} <br />
                                                    <span className="text-gray-500">{log.from_email}</span>
                                                </td>
                                                <td style={{ fontSize: '12px' }}>{log.tenant_business?.business?.name || `Business ${log.tenant_business?.business_id}`}</td>
                                                <td style={{ fontSize: '12px' }}>{log.sent_to}</td>
                                                <td>
                                                    <Badge size="xs" color={log.status === 'sent' ? 'green' : 'red'}>
                                                        {t(log.status)}
                                                    </Badge>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </Tabs.Panel>
            </Tabs>
        </div>
    );
}
